import React, { useEffect, useState } from 'react';
import { PaywallFormElements, PaywallProps } from './paywall.types';
import {
  BackgroundOverlay,
  Wrapper,
  Error,
  Title,
  Text,
  Italic,
} from './paywall.styles';
import dayjs from 'dayjs';

export const Paywall: React.FC<PaywallProps> = ({
  earlyAccessCode,
  publicLiveDate,
}) => {
  const [input, setInput] = useState<string>('');
  const [paywallVisible, setPaywallVisible] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (publicLiveDate) {
      const todayIsBeforeLiveDate = dayjs().isBefore(
        dayjs(publicLiveDate),
        'hour'
      );

      setTimeout(() => {
        if (
          !paywallVisible &&
          earlyAccessCode &&
          publicLiveDate &&
          todayIsBeforeLiveDate
        ) {
          showPaywall();
        }
      }, 1000);
    } else {
      setTimeout(() => {
        if (!paywallVisible && earlyAccessCode) {
          showPaywall();
        }
      }, 1000);
    }

    return () => {
      hidePaywall();
    };
  }, [earlyAccessCode, publicLiveDate]);

  const showPaywall = () => {
    const mainContentContainer: HTMLElement | null = document.querySelector(
      '.main-content-container'
    );
    const paywallWrapper: HTMLElement | null =
      document.querySelector('#limited_access');

    if (paywallWrapper) {
      paywallWrapper.style.bottom = '0px';
    }

    if (mainContentContainer) {
      mainContentContainer.style.transition = '0.3s ease-in-out';
      mainContentContainer.style.filter = 'blur(5px)';
    }

    document.body.style.overflow = 'hidden';
    document.body.style.removeProperty('scroll-behavior');

    // Timeout required to stop race condition between overflow style on body and scrolling resulting in user stuck in the middle of the page.
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    }, 400);

    setPaywallVisible(true);
  };

  const hidePaywall = () => {
    const mainContentContainer: HTMLElement | null = document.querySelector(
      '.main-content-container'
    );
    const paywallWrapper: HTMLElement | null =
      document.querySelector('#limited_access');

    if (paywallWrapper) {
      paywallWrapper.style.bottom = '-400px';
    }

    if (mainContentContainer) {
      mainContentContainer.style.removeProperty('transition');
      mainContentContainer.style.removeProperty('filter');
    }

    document.body.style.removeProperty('overflow');

    setPaywallVisible(false);
  };

  const handleSubmit = (e: React.FormEvent<PaywallFormElements>) => {
    e.preventDefault();
    if (earlyAccessCode.replace(' ', '').split(',').includes(input)) {
      hidePaywall();
      setInput('');
      setErrorMessage(null);
      return;
    }

    if (input.length === 0) {
      setErrorMessage('Please enter an invitation code.');
    }

    if (input.length > 0) {
      setErrorMessage('Invalid code. Please try again.');
    }
  };

  return (
    <>
      {paywallVisible && <BackgroundOverlay />}
      <Wrapper id="limited_access">
        <svg
          width="1189"
          height="1136"
          viewBox="0 0 1189 1136"
          fill="none"
          aria-label="Paywall background image"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M594.5 1136C266.719 1136 0 881.17 0 568C0 254.83 266.719 0 594.5 0C922.281 0 1189 254.83 1189 568C1189 881.17 922.281 1136 594.5 1136ZM594.5 76.9912C311.097 76.9912 80.5832 297.229 80.5832 568C80.5832 838.77 311.097 1059.01 594.5 1059.01C877.903 1059.01 1108.42 838.77 1108.42 568C1108.42 297.229 877.79 76.9912 594.5 76.9912ZM641.261 1042.42C396.22 1042.42 196.805 851.892 196.805 617.773C196.805 383.655 396.106 193.129 641.261 193.129C886.416 193.129 1085.72 383.655 1085.72 617.773C1085.72 851.892 886.302 1042.42 641.261 1042.42ZM641.261 267.951C439.349 267.951 275.118 424.861 275.118 617.773C275.118 810.685 439.349 967.595 641.261 967.595C843.173 967.595 1007.4 810.685 1007.4 617.773C1007.4 424.861 843.173 267.951 641.261 267.951ZM594.5 855.145C429.929 855.145 296.002 727.187 296.002 569.952C296.002 412.716 429.929 284.759 594.5 284.759C759.071 284.759 892.998 412.716 892.998 569.952C892.998 727.187 759.071 855.145 594.5 855.145ZM594.5 357.413C471.809 357.413 372.045 452.73 372.045 569.952C372.045 687.174 471.809 782.491 594.5 782.491C717.191 782.491 816.955 687.174 816.955 569.952C816.955 452.73 717.191 357.413 594.5 357.413Z"
            fill="#0573A8"
          />
        </svg>
        <div>
          <Title>Early access only</Title>
          <Text>This content is not yet public.</Text>
          <Text>
            If you are a network member and you received an invitation to access
            this content, please enter the invitation code below.
          </Text>
          <form onSubmit={handleSubmit}>
            <input
              onChange={e => setInput(e.target.value)}
              placeholder="Enter code..."
              value={input}
              id="earlyAccessCode"
              type="text"
              maxLength={50}
            />
            <button type="submit">Submit</button>
          </form>
          {errorMessage && <Error>{errorMessage}</Error>}

          <Italic>Invitation code delivered to your inbox</Italic>
        </div>
      </Wrapper>
    </>
  );
};
